import { useState, useEffect } from "react";
import { Plus, MessageSquare, Trash2, Users, Building2, Truck } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../../components/ui/dialog";
import { Switch } from "../../components/ui/switch";
import { toast } from "sonner";
import { authFetch } from "../../services/authService";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Opzioni destinatari
const TARGET_OPTIONS = [
  { value: "all", label: "Tutti gli utenti", icon: Users, description: "Invia a ricambisti e officine" },
  { value: "ricambisti", label: "Solo Ricambisti", icon: Truck, description: "Invia solo ai ricambisti" },
  { value: "officine", label: "Solo Officine", icon: Building2, description: "Invia solo alle officine" }
];

export default function AdminCommunications({ user }) {
  const [communications, setCommunications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [formData, setFormData] = useState({
    title: "",
    message: "",
    target_audience: "all", // "all", "ricambisti", "officine"
    target_users: [],
    is_active: true
  });

  useEffect(() => {
    fetchCommunications();
  }, []);

  const fetchCommunications = async () => {
    try {
      const response = await authFetch(`${API}/admin/communications`, { });
      if (response.ok) {
        setCommunications(await response.json());
      }
    } catch (error) {
      toast.error("Errore nel caricamento");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const response = await authFetch(`${API}/admin/communications`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        toast.success("Comunicazione creata");
        setShowDialog(false);
        resetForm();
        fetchCommunications();
      }
    } catch (error) {
      toast.error("Errore nel salvataggio");
    }
  };

  const handleDelete = async (commId) => {
    if (!window.confirm("Sei sicuro di voler eliminare questa comunicazione?")) return;
    
    try {
      const response = await authFetch(`${API}/admin/communications/${commId}`, {
        method: "DELETE"
      });

      if (response.ok) {
        toast.success("Comunicazione eliminata");
        fetchCommunications();
      }
    } catch (error) {
      toast.error("Errore nell'eliminazione");
    }
  };

  const resetForm = () => {
    setFormData({
      title: "",
      message: "",
      target_audience: "all",
      target_users: [],
      is_active: true
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div data-testid="admin-communications-page" className="space-y-6">
      {/* Header */}
      <div className="page-header">
        <h1 className="page-title">Comunicazioni</h1>
        <Dialog open={showDialog} onOpenChange={setShowDialog}>
          <DialogTrigger asChild>
            <Button data-testid="new-communication-btn" className="btn-primary">
              <Plus className="w-4 h-4 mr-2" />
              Nuova Comunicazione
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-zinc-900 border-zinc-800">
            <DialogHeader>
              <DialogTitle className="text-white uppercase font-['Barlow_Condensed']">
                Nuova Comunicazione
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              <div className="form-group">
                <label className="form-label">Titolo *</label>
                <input
                  data-testid="input-comm-title"
                  type="text"
                  className="form-input"
                  value={formData.title}
                  onChange={(e) => setFormData({...formData, title: e.target.value})}
                  placeholder="Titolo della comunicazione"
                  required
                />
              </div>
              <div className="form-group">
                <label className="form-label">Messaggio *</label>
                <textarea
                  data-testid="input-comm-message"
                  className="form-textarea h-32"
                  value={formData.message}
                  onChange={(e) => setFormData({...formData, message: e.target.value})}
                  placeholder="Contenuto del messaggio..."
                  required
                />
              </div>
              
              {/* Selezione Destinatari */}
              <div className="form-group">
                <label className="form-label">Destinatari *</label>
                <div className="grid grid-cols-1 gap-2 mt-2">
                  {TARGET_OPTIONS.map((option) => {
                    const Icon = option.icon;
                    const isSelected = formData.target_audience === option.value;
                    return (
                      <div
                        key={option.value}
                        onClick={() => setFormData({...formData, target_audience: option.value})}
                        className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-all ${
                          isSelected 
                            ? 'border-primary bg-primary/10 text-white' 
                            : 'border-zinc-700 bg-zinc-800/50 text-zinc-400 hover:border-zinc-600'
                        }`}
                        data-testid={`target-${option.value}`}
                      >
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                          isSelected ? 'bg-primary text-black' : 'bg-zinc-700 text-zinc-400'
                        }`}>
                          <Icon className="w-5 h-5" />
                        </div>
                        <div className="flex-1">
                          <p className={`font-medium ${isSelected ? 'text-white' : 'text-zinc-300'}`}>
                            {option.label}
                          </p>
                          <p className="text-xs text-zinc-500">{option.description}</p>
                        </div>
                        <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                          isSelected ? 'border-primary bg-primary' : 'border-zinc-600'
                        }`}>
                          {isSelected && <div className="w-2 h-2 rounded-full bg-black"></div>}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
              
              <div className="flex items-center justify-between">
                <label className="form-label">Attiva</label>
                <Switch
                  checked={formData.is_active}
                  onCheckedChange={(checked) => setFormData({...formData, is_active: checked})}
                />
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <Button type="button" variant="ghost" onClick={() => setShowDialog(false)} className="text-zinc-400">
                  Annulla
                </Button>
                <Button data-testid="submit-comm-btn" type="submit" className="btn-primary">
                  Pubblica
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Communications List */}
      {communications.length > 0 ? (
        <div className="space-y-4">
          {communications.map((comm) => (
            <div key={comm.communication_id} className="bg-zinc-900/50 border border-zinc-800 rounded-sm p-5 card-hover">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <MessageSquare className="w-5 h-5 text-primary" />
                    <h3 className="text-lg font-bold text-white">{comm.title}</h3>
                    <span className={`px-2 py-0.5 rounded-sm text-xs uppercase ${comm.is_active ? "status-accepted" : "status-cancelled"}`}>
                      {comm.is_active ? "Attiva" : "Inattiva"}
                    </span>
                  </div>
                  <p className="text-zinc-400 mb-3">{comm.message}</p>
                  <div className="flex items-center gap-4 text-sm text-zinc-500">
                    <span>
                      Letta da {comm.read_by?.length || 0} utenti
                    </span>
                    <span className="flex items-center gap-1">
                      {comm.target_audience === "ricambisti" ? (
                        <>
                          <Truck className="w-3 h-3" />
                          Solo Ricambisti
                        </>
                      ) : comm.target_audience === "officine" ? (
                        <>
                          <Building2 className="w-3 h-3" />
                          Solo Officine
                        </>
                      ) : comm.target_users?.length > 0 ? (
                        <>
                          <Users className="w-3 h-3" />
                          {comm.target_users.length} destinatari
                        </>
                      ) : (
                        <>
                          <Users className="w-3 h-3" />
                          Tutti gli utenti
                        </>
                      )}
                    </span>
                    <span className="font-['JetBrains_Mono']">
                      {comm.created_at?.split("T")[0]}
                    </span>
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => handleDelete(comm.communication_id)}
                  className="text-zinc-400 hover:text-red-400"
                >
                  <Trash2 className="w-5 h-5" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="empty-state">
          <MessageSquare className="w-16 h-16 empty-state-icon" />
          <p className="empty-state-title">Nessuna comunicazione</p>
          <p className="empty-state-text">Crea comunicazioni per gli utenti</p>
        </div>
      )}
    </div>
  );
}
